import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Lock, X, CheckCircle, Pulse, ArrowRight } from "@phosphor-icons/react";
import { api } from "../lib/api";

/**
 * SoftPaywallGate — LinkedIn/Medium/Quora-style "you've seen enough, sign up" popup
 * for anonymous visitors. Centered modal with backdrop (not a bottom sheet).
 *
 * iter220 — Switched from a blocklist to an ALLOWLIST. The gate used to fire on
 * every public page except a hand-maintained exempt list, so it leaked onto
 * info/marketing pages (about, press, contact, developer, security, events,
 * learn, …) where a "sign up to continue" popup is pure friction. Now it fires
 * ONLY on the high-value content surfaces where the "keep reading" ask converts:
 * Q&A, news articles, user profiles, tags, community subforums, and public
 * company profiles.
 *
 * Triggers on whichever happens first:
 *   • Time-on-page (6s)
 *   • Scroll depth (25%)
 *   • 3 meaningful clicks on a content card
 *
 * Skipped entirely for:
 *   • Logged-in users (caller doesn't render us)
 *   • Search-engine crawlers (Googlebot, Bingbot, etc.) — must stay SEO-friendly
 *   • Every page NOT in GATED_PREFIXES (which is now the vast majority)
 */

// Only these prefixes ever show the gate. Everything else is popup-free.
// iter-current: extended to the other high-value content surfaces — bounty
// program details, job details, lab details and CTF challenge details.
// NOTE: public profiles (/u/) are deliberately NOT gated — profiles are open;
// only messaging/contact/socials on them are login-gated (server-side).
const GATED_PREFIXES = ["/q/", "/news/", "/t/", "/h/", "/companies/", "/programs/", "/jobs/", "/labs/", "/ctf/"];
// …except conversion/SEO-landing surfaces that happen to sit under a gated prefix.
const GATE_EXCLUDE = ["/companies/new", "/labs/category"];

// Crawler UA fragments — extend as needed
const CRAWLER_UA_FRAGMENTS = [
    "googlebot", "bingbot", "slurp", "duckduckbot", "baiduspider",
    "yandexbot", "facebookexternalhit", "twitterbot", "linkedinbot",
    "applebot", "petalbot", "ahrefsbot", "semrushbot", "mj12bot",
    "headlesschrome", "lighthouse", "chrome-lighthouse",
];

function isCrawler() {
    if (typeof navigator === "undefined") return true;
    const ua = (navigator.userAgent || "").toLowerCase();
    return CRAWLER_UA_FRAGMENTS.some((frag) => ua.includes(frag));
}

function isGatedPath(pathname) {
    if (GATE_EXCLUDE.some((p) => pathname === p || pathname.startsWith(p + "/"))) return false;
    return GATED_PREFIXES.some((p) => pathname.startsWith(p));
}

// Per-session dismissal — once they dismiss on a page, don't show again for that path
const DISMISS_KEY = "hc_paywall_dismissed";
function loadDismissed() {
    try { return JSON.parse(sessionStorage.getItem(DISMISS_KEY) || "{}"); } catch { return {}; }
}
function markDismissed(pathname) {
    const map = loadDismissed();
    map[pathname] = Date.now();
    try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify(map)); } catch { /* private mode */ }
}

// iter220 — "first content page is free". We record the distinct gated pages a
// visitor opens this session; the gate only arms from the SECOND gated page
// onward. First touch feels open (Medium/Quora pattern) — the ask lands once
// they're clearly engaged, which lifts signups without feeling pushy.
const VIEWS_KEY = "hc_gated_views";
function loadGatedViews() {
    try { return JSON.parse(sessionStorage.getItem(VIEWS_KEY) || "[]"); } catch { return []; }
}
/** Records this gated path and returns the total distinct gated pages seen. */
function recordGatedView(pathname) {
    const seen = loadGatedViews();
    if (!seen.includes(pathname)) {
        seen.push(pathname);
        try { sessionStorage.setItem(VIEWS_KEY, JSON.stringify(seen)); } catch { /* private mode */ }
    }
    return seen.length;
}

export default function SoftPaywallGate() {
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);
    const [online, setOnline] = useState(null);
    const interactionsRef = useRef(0);
    const cardRef = useRef(null);
    const triggerElRef = useRef(null);

    // Focus management: move focus into the dialog, trap Tab, restore on close.
    useEffect(() => {
        if (!open) return undefined;
        triggerElRef.current = document.activeElement;
        const card = cardRef.current;
        card?.focus();
        const onKey = (e) => {
            if (e.key !== "Tab" || !card) return;
            const f = card.querySelectorAll("a[href], button:not([disabled])");
            if (!f.length) return;
            const first = f[0];
            const last = f[f.length - 1];
            if (!card.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        };
        document.addEventListener("keydown", onKey, true);
        return () => {
            document.removeEventListener("keydown", onKey, true);
            try { triggerElRef.current?.focus?.(); } catch { /* trigger may be gone */ }
        };
    }, [open]);

    // Live social proof — fetched once the modal actually opens.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        api.get("/stats/online-now")
            .then(({ data }) => { if (!cancelled) setOnline(data?.count); })
            .catch(() => { /* optional */ });
        return () => { cancelled = true; };
    }, [open]);

    useEffect(() => {
        // Reset on path change — fresh page, fresh gate
        setOpen(false);
        interactionsRef.current = 0;

        if (isCrawler() || !isGatedPath(pathname)) return;
        if (loadDismissed()[pathname]) return;   // already dismissed for this path in this session

        // First content page of the session is free — arm the gate only from the
        // 2nd distinct gated page onward.
        const viewCount = recordGatedView(pathname);
        if (viewCount < 2) return;

        const timerMs = 6000;
        const scrollPct = 0.25;

        const trigger = () => setOpen(true);

        const t = setTimeout(trigger, timerMs);

        const onScroll = () => {
            const doc = document.documentElement;
            const denom = Math.max(1, doc.scrollHeight - doc.clientHeight);
            const pct = (doc.scrollTop || window.scrollY || 0) / denom;
            if (pct >= scrollPct) trigger();
        };
        window.addEventListener("scroll", onScroll, { passive: true });

        // Click on a content card — fires earlier than the timer on browse pages
        const onClick = (e) => {
            const target = e.target.closest("[data-content-card], a[href^='/q/'], a[href^='/news/'], a[href^='/u/'], a[href^='/c/'], a[href^='/h/']");
            if (target) {
                interactionsRef.current += 1;
                if (interactionsRef.current >= 3) trigger();
            }
        };
        document.addEventListener("click", onClick, true);

        return () => {
            clearTimeout(t);
            window.removeEventListener("scroll", onScroll);
            document.removeEventListener("click", onClick, true);
        };
    }, [pathname]);

    // Scroll-lock body when modal is open
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    // ESC closes the modal (counts as dismiss)
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") {
                markDismissed(pathname);
                setOpen(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, pathname]);

    if (!open) return null;

    const dismiss = () => {
        markDismissed(pathname);
        setOpen(false);
    };

    const next = encodeURIComponent(pathname);

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-200"
            role="dialog"
            aria-modal="true"
            aria-labelledby="soft-paywall-title"
            data-testid="soft-paywall"
            onClick={dismiss}
        >
            {/* Backdrop with blur so content underneath teases but doesn't read */}
            <div className="absolute inset-0 bg-[#0b0f19]/85 backdrop-blur-md" aria-hidden="true" />

            {/* Modal card */}
            <div
                ref={cardRef}
                tabIndex={-1}
                className="relative w-full max-w-md bg-[#0d1220] border border-white/10 rounded-md overflow-hidden shadow-[0_0_60px_-15px_rgba(34,211,238,0.25)] focus:outline-none"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Top accent strip */}
                <div className="h-1 bg-gradient-to-r from-cyan-400 via-cyan-500/60 to-transparent" aria-hidden="true" />

                <div className="p-6 sm:p-8">
                    {/* Close (counts as dismiss) */}
                    <button
                        onClick={dismiss}
                        aria-label="Close sign-in prompt"
                        data-testid="soft-paywall-dismiss"
                        className="absolute top-4 right-3 text-slate-500 hover:text-slate-200 inline-flex items-center justify-center w-7 h-7 rounded-sm hover:bg-slate-800/60"
                    >
                        <X size={14} weight="bold" />
                    </button>

                    <div className="flex items-center gap-2.5 mb-5">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-sm border border-cyan-500/40 bg-cyan-500/10">
                            <Lock size={15} weight="duotone" className="text-cyan-300" />
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-cyan-400/90">
                            // members only
                        </span>
                    </div>

                    <h3
                        id="soft-paywall-title"
                        className="text-2xl sm:text-3xl font-black text-slate-100 leading-tight"
                        style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}
                    >
                        keep reading on hacking.community<span className="text-cyan-400">.</span>
                    </h3>

                    <ul className="mt-4 space-y-2">
                        {[
                            "full answers, write-ups & CVE breakdowns",
                            "solve hands-on labs · capture flags · earn karma",
                            "encrypted DMs with working hackers",
                        ].map((line) => (
                            <li key={line} className="flex items-start gap-2 text-[13px] text-slate-300">
                                <CheckCircle size={15} weight="fill" className="text-cyan-400 shrink-0 mt-0.5" />
                                <span>{line}</span>
                            </li>
                        ))}
                    </ul>

                    {online != null && (
                        <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono text-emerald-300 border border-emerald-500/30 bg-emerald-500/5 px-2 py-1 rounded-sm" data-testid="soft-paywall-online">
                            <Pulse size={11} weight="duotone" className="animate-pulse" />
                            {online.toLocaleString()} hackers online now
                        </div>
                    )}

                    <div className="mt-6 flex flex-col gap-2">
                        <Link
                            to={`/login?next=${next}&mode=register`}
                            data-testid="soft-paywall-register"
                            className="group bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold text-xs uppercase tracking-widest px-5 py-3.5 rounded-sm inline-flex items-center justify-center gap-2 whitespace-nowrap"
                        >
                            create free account
                            <ArrowRight size={14} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                        <Link
                            to={`/login?next=${next}`}
                            data-testid="soft-paywall-login"
                            className="border border-slate-700 hover:border-cyan-500/60 text-slate-200 hover:text-cyan-200 text-xs uppercase tracking-widest font-mono px-5 py-3 rounded-sm inline-flex items-center justify-center whitespace-nowrap"
                        >
                            sign in
                        </Link>
                    </div>

                    <button
                        onClick={dismiss}
                        data-testid="soft-paywall-not-now"
                        className="mt-4 w-full text-[10px] uppercase tracking-widest font-mono text-slate-500 hover:text-slate-300"
                    >
                        not now
                    </button>

                    <p className="mt-5 pt-4 border-t border-white/5 text-[9px] sm:text-[10px] uppercase tracking-widest font-mono text-slate-600 text-center whitespace-nowrap overflow-hidden text-ellipsis">
                        // free forever · no spam · e2e-encrypted dms
                    </p>
                </div>
            </div>
        </div>
    );
}
